import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Activity, AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import Button from '@/components/button'
import ChipSelect from '@/components/chip-select'
import EmptyState from '@/components/empty-state'
import PageHeader from '@/components/page-header'
import Skeleton from '@/components/skeleton'
import StatusBadge from '@/components/status-badge'
import {
  useAlertasDashboardQuery,
  useIndicadoresDashboardQuery,
  usePreferenciaIndicadoresQuery,
  useSaudeDashboardQuery,
} from '@/http/queries/dashboard'
import { useAtualizarPreferenciaIndicadoresMutation } from '@/http/mutations/dashboard'
import {
  FAMILIA_INDICADOR,
  estadoSaudeProdutoMeta,
  familiaIndicadorMeta,
  produtoDashboardLabel,
  severidadeAlertaTone,
} from '@/types/dashboard'
import type { AlertaDashboard, FamiliaIndicador } from '@/types/dashboard'
import { PERFIL, SITUACOES_ATENCAO, situacaoGuiaMeta } from '@/types/domain'
import { filtroDeAlerta } from '@/views/autorizacoes/list/filtro'
import { toErrorMessage } from '@/services/errors'
import { useAuth } from '@/utility/context/auth-context'
import { formatNumero, formatRelative, orDash } from '@/utility/Utils'
import styles from './style.module.css'

interface IndicadorCardMeta {
  familia: FamiliaIndicador
  icone: LucideIcon
  tom: 'success' | 'info' | 'warning' | 'danger'
  descricao: string
}

const INDICADORES: IndicadorCardMeta[] = [
  {
    familia: FAMILIA_INDICADOR.Concluidas,
    icone: CheckCircle2,
    tom: 'success',
    descricao: 'Guias autorizadas, parcialmente autorizadas e executadas no período',
  },
  {
    familia: FAMILIA_INDICADOR.EmAndamento,
    icone: Clock,
    tom: 'info',
    descricao: 'Aguardando anexo, assinatura, biometria, token, execução ou cancelamento',
  },
  {
    familia: FAMILIA_INDICADOR.PrecisamDeAtencao,
    icone: XCircle,
    tom: 'danger',
    descricao: 'Negadas, não processadas, inconsistência no convênio e dados inválidos',
  },
  {
    familia: FAMILIA_INDICADOR.Canceladas,
    icone: Activity,
    tom: 'info',
    descricao: 'Canceladas',
  },
]

const PERFIL_DA_PREFERENCIA = PERFIL.Operador

const FAMILIA_OPTIONS = Object.values(FAMILIA_INDICADOR).map((familia) => ({
  value: familia,
  label: familiaIndicadorMeta[familia],
}))

const Dashboard = () => {
  const { isAdmin, user } = useAuth()
  const navigate = useNavigate()

  const indicadoresQuery = useIndicadoresDashboardQuery()
  const saudeQuery = useSaudeDashboardQuery()
  const alertasQuery = useAlertasDashboardQuery()

  const preferenciaQuery = usePreferenciaIndicadoresQuery(PERFIL_DA_PREFERENCIA, isAdmin)
  const atualizarPreferencia = useAtualizarPreferenciaIndicadoresMutation()

  const [familiasSelecionadas, setFamiliasSelecionadas] = useState<string[] | null>(null)
  const familiasAtuais = familiasSelecionadas ?? preferenciaQuery.data ?? []

  const indicadores = indicadoresQuery.data
  const produtos = saudeQuery.data ?? []
  const alertas = alertasQuery.data ?? []

  const quantidadePorCodigo = useMemo(
    () => new Map((indicadores?.porCodigo ?? []).map((item) => [item.codigo, item.quantidade])),
    [indicadores],
  )

  const mostrarQuantidadeDasSituacoes =
    indicadores !== undefined && indicadores.precisamDeAtencao !== null

  const abrirRecorteDoAlerta = (alerta: AlertaDashboard) => {
    void navigate('/autorizacoes', { state: { filtro: filtroDeAlerta(alerta.filtro) } })
  }

  const valorIndicador = (familia: FamiliaIndicador): number | null => {
    if (!indicadores) return null
    switch (familia) {
      case FAMILIA_INDICADOR.Concluidas:
        return indicadores.concluidas
      case FAMILIA_INDICADOR.EmAndamento:
        return indicadores.emAndamento
      case FAMILIA_INDICADOR.PrecisamDeAtencao:
        return indicadores.precisamDeAtencao
      case FAMILIA_INDICADOR.Canceladas:
        return indicadores.canceladas
      default:
        return null
    }
  }

  return (
    <>
      <PageHeader
        title={`Olá, ${user?.nomeUsuario ?? 'time'}`}
        subtitle={
          isAdmin
            ? 'Panorama do ecossistema IntegraDoor e da operação'
            : 'Panorama da operação'
        }
      />

      <p className={styles.notaBloco}>Indicadores das últimas 24 horas.</p>

      <section className={styles.grid}>
        {INDICADORES.map((indicador) => {
          const valor = valorIndicador(indicador.familia)
          const semAcesso =
            !indicadoresQuery.isLoading && !indicadoresQuery.error && Boolean(indicadores) && valor === null

          return (
            <article className={styles.card} key={indicador.familia}>
              <header className={styles.cardHeader}>
                <span className={styles[indicador.tom]}>
                  <indicador.icone size={18} />
                </span>
                <h3 className={styles.cardTitulo}>{familiaIndicadorMeta[indicador.familia]}</h3>
              </header>
              {indicadoresQuery.isLoading ? (
                <Skeleton width={90} height={34} rounded={8} />
              ) : semAcesso ? (
                <StatusBadge label="Sem acesso a este indicador" tone="neutral" />
              ) : (
                <strong className={styles.cardValor}>{formatNumero(valor)}</strong>
              )}
              <p className={styles.cardDescricao}>{indicador.descricao}</p>
            </article>
          )
        })}
      </section>

      {indicadoresQuery.error ? (
        <EmptyState
          icon={<AlertTriangle size={20} />}
          title="Não foi possível carregar os indicadores"
          description={toErrorMessage(indicadoresQuery.error)}
          action={
            <Button variant="outline" size="sm" onClick={() => void indicadoresQuery.refetch()}>
              Tentar novamente
            </Button>
          }
        />
      ) : null}

      <section className={styles.bloco}>
        <h2 className={styles.blocoTitulo}>Saúde dos produtos</h2>

        {saudeQuery.isLoading ? (
          <div className={styles.produtos}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={52} rounded={8} />
            ))}
          </div>
        ) : saudeQuery.error ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title="Não foi possível carregar a saúde dos produtos"
            description={toErrorMessage(saudeQuery.error)}
            action={
              <Button variant="outline" size="sm" onClick={() => void saudeQuery.refetch()}>
                Tentar novamente
              </Button>
            }
          />
        ) : (
          <div className={styles.produtos}>
            {produtos.map((produto) => {
              const meta = estadoSaudeProdutoMeta[produto.estado]
              return (
                <div className={styles.produto} key={produto.produto}>
                  <div className={styles.produtoInfo}>
                    <span className={styles.produtoNome}>
                      {produtoDashboardLabel[produto.produto] ?? produto.produto}
                    </span>
                    <span className={styles.produtoDetalhe}>
                      {produto.ultimaVerificacaoUtc
                        ? `Verificado ${formatRelative(produto.ultimaVerificacaoUtc)}`
                        : 'Nunca verificado'}
                      {produto.latenciaMs !== null ? ` · ${produto.latenciaMs}ms` : ''}
                    </span>
                  </div>
                  <StatusBadge label={meta.label} tone={meta.tone} />
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className={styles.bloco}>
        <h2 className={styles.blocoTitulo}>Alertas</h2>

        {alertasQuery.isLoading ? (
          <Skeleton variant="text" count={3} />
        ) : alertasQuery.error ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title="Não foi possível carregar os alertas"
            description={toErrorMessage(alertasQuery.error)}
            action={
              <Button variant="outline" size="sm" onClick={() => void alertasQuery.refetch()}>
                Tentar novamente
              </Button>
            }
          />
        ) : alertas.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 size={24} />}
            title="Nenhum alerta no momento"
            description="Nenhuma detecção de erro massivo por operadora, credencial ou crawler."
          />
        ) : (
          <div className={styles.alertas}>
            {alertas.map((alerta) => (
              <button
                type="button"
                className={`${styles.alerta} ${styles.alertaAcionavel}`}
                key={`${alerta.tipo}-${alerta.entidadeId ?? alerta.entidadeNome ?? ''}`}
                onClick={() => abrirRecorteDoAlerta(alerta)}
                title="Abrir o recorte na listagem de autorizações"
              >
                <span className={styles.alertaCabecalho}>
                  <StatusBadge
                    label={alerta.severidade}
                    tone={severidadeAlertaTone[alerta.severidade] ?? 'neutral'}
                  />
                  <strong>{alerta.titulo}</strong>
                </span>
                <span className={styles.alertaDescricao}>{alerta.descricao}</span>
                <span className={styles.alertaMeta}>
                  {orDash(alerta.entidadeNome)} ·{' '}
                  {alerta.quantidade === 1
                    ? '1 ocorrência'
                    : `${formatNumero(alerta.quantidade)} ocorrências`}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className={styles.bloco}>
        <h2 className={styles.blocoTitulo}>Situações que exigem atenção</h2>
        <div className={styles.situacoes}>
          {SITUACOES_ATENCAO.map((situacao) => (
            <StatusBadge
              key={situacao}
              label={
                mostrarQuantidadeDasSituacoes
                  ? `${situacaoGuiaMeta[situacao].label}: ${formatNumero(quantidadePorCodigo.get(situacao) ?? 0)}`
                  : situacaoGuiaMeta[situacao].label
              }
              tone={situacaoGuiaMeta[situacao].tone}
            />
          ))}
        </div>
      </section>

      {isAdmin ? (
        <section className={styles.bloco}>
          <h2 className={styles.blocoTitulo}>Indicadores liberados para o Operador</h2>
          <p className={styles.notaBloco}>
            Escolha quais indicadores o Operador enxerga no dashboard.
          </p>

          {preferenciaQuery.isLoading ? (
            <Skeleton variant="input" count={1} />
          ) : (
            <>
              <ChipSelect
                options={FAMILIA_OPTIONS}
                value={familiasAtuais}
                onChange={setFamiliasSelecionadas}
                ariaLabel="Indicadores liberados para o Operador"
              />

              <Button
                variant="primary"
                size="sm"
                className={styles.botaoSalvarPreferencia}
                isLoading={atualizarPreferencia.isPending}
                onClick={() =>
                  atualizarPreferencia.mutate(
                    { perfil: PERFIL_DA_PREFERENCIA, familias: familiasAtuais as FamiliaIndicador[] },
                    { onSuccess: () => setFamiliasSelecionadas(null) },
                  )
                }
              >
                Salvar preferência
              </Button>
            </>
          )}
        </section>
      ) : null}
    </>
  )
}

export default Dashboard
